<template>
    <div
        v-if="isLoaded && !offers.length"
        class="box-body with-border clearfix"
    >
        <p>
            {{ $t('messages.offer.noOffers') }}
            <a href="/offers/new">
                {{ $t('addNew') }}
            </a>
        </p>
    </div>

    <div v-else>
        <div
            v-if="paginationOptions.total"
            class="box-body with-border clearfix"
        >
            <BadgeTotal
                :value="paginationOptions.total"
                color="gray"
                :label="`${$t('total')}: `"
            />

            <div
                v-if="paginationOptions.last_page > 1"
                class="pagination-box"
            >
                <PaginationTable
                    :options="paginationOptions"
                    @change-page="fetchOffers"
                />
            </div>
        </div>

        <div class="box-body table-responsive no-padding">
            <div
                v-if="isLoading"
                class="box-body with-border clearfix"
            >
                <tr>
                    <td>
                        <img
                            class="table-row-preloader"
                            rel="preload"
                            src="/img/table-row-preloader.gif"
                        />
                    </td>
                </tr>
            </div>

            <table
                v-else
                class="table table-striped table-hover"
            >
                <thead>
                    <tr>
                        <th>{{ $t('id') }}</th>

                        <th>{{ $t('name') }}</th>

                        <th>{{ $t('group') }}</th>

                        <th>{{ $t('url') }}</th>

                        <th>{{ $t('redirectUrl') }}</th>

                        <th>{{ $t('offer.nextOffer') }}</th>

                        <th class="text-center">{{ $t('redirect') }}<br />{{ $t('desktop') }}</th>

                        <th class="text-center">{{ $t('compare') }}<br />{{ $t('clickerData') }}</th>

                        <th class="text-center">{{ $t('active') }}</th>

                        <th class="text-center">{{ $t('check') }}</th>

                        <th class="text-center">{{ $t('campaign.campaigns') }}</th>

                        <th class="text-center">{{ $t('tags') }}</th>

                        <th class="text-center">{{ $t('message.messages') }}</th>

                        <th class="text-center">{{ $t('clicks') }}</th>

                        <th class="text-right">{{ $t('lifetime') }}</th>

                        <th class="text-right">{{ $t('createdAt') }}</th>

                        <th></th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="offer in offers"
                        :key="offer.id"
                    >
                        <td>{{ offer.id }}</td>

                        <td>{{ offer.name }}</td>

                        <td v-if="offer.category && offer.category.group">
                            <a :href="`/category-groups/${offer.category.group_id}`">
                                {{ offer.category.group.name }}
                            </a>
                        </td>

                        <td v-else>
                            <em class="text-info">{{ $t('none') }}</em>
                        </td>

                        <td>
                            <a
                                :href="offer.url"
                                class="o-cut"
                                target="_blank"
                            >
                                {{ offer.url }}
                            </a>
                        </td>

                        <td v-if="offer.redirect_url">
                            <a
                                :href="offer.redirect_url"
                                class="o-cut"
                                target="_blank"
                            >
                                {{ offer.redirect_url }}
                            </a>
                        </td>

                        <td v-else>
                            <em class="text-info">{{ $t('none') }}</em>
                        </td>

                        <td v-if="offer.next_offer">
                            <a :href="`/offers/${offer.next_offer_id}`">
                                {{ offer.next_offer.name }}
                            </a>
                        </td>

                        <td v-else>
                            <em class="text-info">{{ $t('none') }}</em>
                        </td>

                        <td class="text-center">
                            <ValueYesNo :value="offer.redirect_desktop" />
                        </td>

                        <td class="text-center">
                            <ValueYesNo :value="offer.compare_clicker_data" />
                        </td>

                        <td class="text-center">
                            <ValueYesNo :value="offer.active" />
                        </td>

                        <td class="text-center">
                            <ValueYesNo
                                :value="offer.check ? offer.check.is_live : null"
                                :title="$t('liveCheck')"
                            />

                            <YesOrNone
                                :value="offer.check ? offer.check.is_checked : null"
                                :title="$t('buttonCheck')"
                            />
                        </td>

                        <td class="text-center">
                            <BadgeTotal
                                :value="offer.campaigns_count"
                                color="gray"
                            />
                        </td>

                        <td class="text-center">
                            <BadgeTotal
                                :value="offer.tags_count || 0"
                                color="gray"
                            />
                        </td>

                        <td>
                            <img
                                v-if="isLoadingStats"
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />

                            <template v-else>
                                <BadgeTotal
                                    :value="offer.messages_count"
                                    color="gray"
                                    :title="$t('total')"
                                />

                                <BadgeTotal
                                    :value="offer.messages_sent"
                                    color="green"
                                    :title="$t('sent')"
                                />

                                <BadgeTotal
                                    :value="offer.messages_failed"
                                    color="red"
                                    :title="$t('failed')"
                                />

                                <BadgeTotal
                                    :value="offer.messages_blacklisted"
                                    color="black"
                                    :title="$t('blacklisted')"
                                />
                            </template>
                        </td>

                        <td>
                            <img
                                v-if="isLoadingStats"
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />

                            <template v-else>
                                <BadgeTotal
                                    :value="offer.percentage"
                                    color="light-blue"
                                    :title="$t('amount')"
                                />

                                <BadgeTotal
                                    :value="offer.clicks_count"
                                    color="aqua-active"
                                    :title="$t('amount')"
                                />
                            </template>
                        </td>

                        <td class="text-right">
                            <span v-if="!offer.lifetime">
                                <em class="text-info">{{ $t('infinite') }}</em>
                            </span>

                            <span v-else>{{ offer.formatted_time }}</span>
                        </td>

                        <td class="text-right">
                            {{ formatDate(offer.created_at) }}
                        </td>

                        <td class="text-right text-nowrap">
                            <a
                                v-if="offer.is_editable"
                                :href="`/offers/${offer.id}/edit`"
                                class="btn btn-sm btn-primary"
                            >
                                <i class="fa fa-edit"></i>
                                <span class="sr-only">{{ $t('edit') }}</span>
                            </a>

                            <a
                                :href="`/offers/${offer.id}`"
                                class="btn btn-sm btn-info"
                            >
                                <i class="fa fa-eye"></i>
                                <span class="sr-only">{{ $t('show') }}</span>
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div
            v-if="paginationOptions.last_page > 1"
            class="box-footer clearfix"
        >
            <div class="pagination-box">
                <PaginationTable
                    :options="paginationOptions"
                    @change-page="fetchOffers"
                />
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import PaginationTable from '@/components/subcomponents/PaginationTable';
import ValueYesNo from '@/components/subcomponents/ValueYesNo';
import YesOrNone from '@/components/subcomponents/YesOrNone';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import formatsMixin from '@/mixins/formatsMixin';

export default {
    components: {
        PaginationTable,
        ValueYesNo,
        YesOrNone,
        BadgeTotal,
    },
    mixins: [formatsMixin],
    data() {
        return {
            isLoading: true,
            isLoaded: false,
            isLoadingStats: false,
            offers: [],
            ids: [],
            paginationOptions: {
                disabled: true,
                total: 0,
            },
        };
    },
    async created() {
        await this.fetchOffers(this.$route.query.page);

        this.isLoaded = true;
        this.$emit('loaded');

        EventBus.$on('relodList', () => {
            this.fetchOffers(this.$route.query.page);
        });
    },
    methods: {
        async fetchOffers(page = 1) {
            this.isLoading = true;

            try {
                this.$router
                    .replace({
                        query: {
                            ...this.$route.query,
                            page: page > 1 ? page : undefined,
                        },
                    })
                    .catch(() => {});

                this.paginationOptions.disabled = true;

                const {
                    data: {
                        offers: { data: offers, ...paginationOptions },
                        ids,
                    },
                } = await axios.get(`${this.$route.path}/data`, {
                    params: {
                        page: page,
                        filters: {
                            search: this.$route.query['filter[search]'],
                        },
                        orderBy: this.orderBy,
                        orderByDirection: this.orderByDirection,
                    },
                });

                this.offers = offers;
                this.paginationOptions = paginationOptions;
                this.ids = ids || [];

                if (this.ids.length) {
                    await this.fetchStats(this.ids);
                }
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.paginationOptions.disabled = false;
                this.isLoading = false;
            }
        },
        async fetchStats(ids) {
            try {
                this.isLoadingStats = true;

                const {
                    data: { stats },
                } = await axios.get(`/offers/stats?ids=${ids}`);

                this.offers = this.offers.map((offer) => {
                    const stat = stats.find((stat) => stat.id === offer.id);

                    if (stat) {
                        offer = {
                            ...offer,
                            ...stat,
                        };
                    }

                    return offer;
                });
            } finally {
                this.isLoadingStats = false;
            }
        },
    },
};
</script>
